import { X } from "lucide-react";
import React from "react";

import type { ContextNode } from "../../types/contextGraph";
import { STATUS_CHIP_CLASSES } from "../Cabinet/cabinetTheme";

export interface ContextGraphNodePanelProps {
	node: ContextNode;
	/** For a "+N" card, the real nodes it stands in for. */
	members?: readonly ContextNode[];
	/** Opens a `doppler://` href — the page wires the shared URI opener in. */
	onOpenHref: (href: string, filename?: string) => void;
	onClose: () => void;
}

/** Kind → the eyebrow above the node label. */
const KIND_LABELS: Record<ContextNode["kind"], string> = {
	fund: "Fund",
	company: "Company",
	period: "Period",
	document: "Document",
	clause: "Clause",
	metric: "Metric",
	covenant: "Covenant",
	status: "Status",
};

/** The version rows a document carries in `meta.versions`. */
interface VersionRow {
	label: string;
	status: string | null;
}

function parseVersions(meta: Record<string, unknown>): VersionRow[] {
	const raw = meta.versions;
	if (!Array.isArray(raw)) return [];
	const rows: VersionRow[] = [];
	for (const item of raw) {
		if (typeof item !== "object" || item === null) continue;
		const record = item as Record<string, unknown>;
		const label =
			typeof record.label === "string"
				? record.label
				: typeof record.filename === "string"
					? record.filename
					: null;
		if (label === null) continue;
		rows.push({
			label,
			status: typeof record.status === "string" ? record.status : null,
		});
	}
	return rows;
}

/** Reuses the Cabinet chip so a status means the same thing on both surfaces. */
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
	const classes =
		STATUS_CHIP_CLASSES[status as keyof typeof STATUS_CHIP_CLASSES] ??
		"bg-gray-100 text-gray-600 border-gray-300";
	return (
		<span
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${classes}`}
		>
			{status}
		</span>
	);
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div className="mt-3">
		<div className="text-xs uppercase tracking-wider text-text-muted">{label}</div>
		<div className="mt-0.5 text-sm text-text-base">{children}</div>
	</div>
);

function stringField(meta: Record<string, unknown>, key: string): string | null {
	const value = meta[key];
	if (typeof value === "string" && value.length > 0) return value;
	if (typeof value === "number" && Number.isFinite(value)) return String(value);
	return null;
}

/**
 * Detail panel for the selected card.
 *
 * A document shows what the cabinet knows about it — type and version chain. A
 * clause, metric or covenant shows the evidence behind the number: the value,
 * the quote it came from, the page, and the way back into the PDF. Everything
 * else gets the little the contract carries, rather than an empty panel.
 */
const ContextGraphNodePanel: React.FC<ContextGraphNodePanelProps> = ({
	node,
	members = [],
	onOpenHref,
	onClose,
}) => {
	const versions = parseVersions(node.meta);
	const docType = stringField(node.meta, "doc_type");
	const filename = stringField(node.meta, "filename");
	const company = stringField(node.meta, "company");
	const value = stringField(node.meta, "value") ?? node.sublabel ?? null;
	const quote = stringField(node.meta, "quote");
	const page = stringField(node.meta, "page");
	const section = stringField(node.meta, "section");
	const document = stringField(node.meta, "document");
	const formula = stringField(node.meta, "formula");
	const sourceLabel = stringField(node.meta, "source_label");
	const qualifier = stringField(node.meta, "qualifier");
	const derived = node.meta.derived === true;
	const tied = node.meta.tied === true;

	return (
		<aside
			aria-label="Node details"
			className="flex h-full w-full flex-col overflow-y-auto border-l border-gray-200 bg-white"
		>
			<div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
				<div>
					<div className="text-xs uppercase tracking-wider text-text-muted">
						{KIND_LABELS[node.kind]}
					</div>
					<h2 className="mt-0.5 text-lg font-semibold text-text-strong">
						{node.label}
					</h2>
				</div>
				<button
					type="button"
					aria-label="Close details"
					onClick={onClose}
					className="rounded p-1 text-text-muted hover:bg-gray-100"
				>
					<X size={16} />
				</button>
			</div>

			<div className="px-4 pb-4">
				{node.status && (
					<div className="mt-3">
						<StatusChip status={node.status} />
					</div>
				)}

				{members.length > 0 ? (
					// A "+N" card: what it stands in for, by name, so nothing the
					// collapse hid is unaccounted for.
					<Field label={`Folded in (${members.length})`}>
						<ul className="space-y-1.5">
							{members.map((member) => {
								// A card shared across borrowers is only honest if
								// opening it says whose each file is. The company
								// outranks the period here: on a "Credit Agreement"
								// card the reader already knows the kind and wants
								// the owner.
								const owner =
									stringField(member.meta, "company") ?? member.sublabel;
								const file = stringField(member.meta, "filename");
								return (
									<li key={member.id}>
										<div className="flex items-baseline justify-between gap-2">
											<span className="truncate">{member.label}</span>
											{owner && (
												<span className="shrink-0 text-xs text-text-muted">
													{owner}
												</span>
											)}
										</div>
										{file && file !== member.label && (
											<div className="truncate text-xs text-text-muted">
												{file}
											</div>
										)}
									</li>
								);
							})}
						</ul>
					</Field>
				) : node.kind === "document" ? (
					<>
						{/* The file itself, verbatim — extension, company prefix and
						    all. The card's label is a shortened reading of this;
						    when a reader asks "which file is this", they mean the
						    name on disk. */}
						{filename && (
							<Field label="File">
								<span className="break-all">{filename}</span>
							</Field>
						)}
						{company && <Field label="Company">{company}</Field>}
						{docType && <Field label="Type">{docType.replace(/_/g, " ")}</Field>}
						<Field label="Versions">
							{versions.length === 0 ? (
								<span className="text-text-muted">
									No version chain recorded.
								</span>
							) : (
								<ul className="space-y-1.5">
									{versions.map((version) => (
										<li
											key={version.label}
											className="flex items-center justify-between gap-2"
										>
											<span className="truncate">{version.label}</span>
											{version.status && <StatusChip status={version.status} />}
										</li>
									))}
								</ul>
							)}
						</Field>
					</>
				) : (
					<>
						{value && <Field label="Value">{value}</Field>}
						{/* The same provenance the chat's citation card shows: which
						    paper, which page, which words. */}
						{/* The document's name IS the link into it, drawn the way the
						    chat draws a cited phrase (teal, ruled underneath, tint on
						    hover — restated here because the chat's class is scoped to
						    its markdown container), opening the in-app viewer
						    on the cited page with the cited words highlighted. The
						    internal `doppler://` address is carried, never shown. */}
						{document && (
							<Field label="Document">
								{node.href ? (
									<a
										href={node.href}
										className="cursor-pointer font-medium text-[#003a39] underline decoration-[rgba(0,76,75,0.35)] decoration-[1.5px] underline-offset-2 hover:bg-[rgba(0,76,75,0.1)] rounded-[3px] break-words"
										onClick={(event) => {
											event.preventDefault();
											onOpenHref(node.href as string, filename ?? document ?? undefined);
										}}
									>
										{document}
									</a>
								) : (
									<span className="break-words">{document}</span>
								)}
								{page && <span className="text-text-muted"> · page {page}</span>}
							</Field>
						)}
						{!document && page && <Field label="Page">{page}</Field>}
						{section && <Field label="Section">{section}</Field>}
						{quote && (
							<Field label="Cited text">
								<blockquote className="whitespace-pre-line border-l-2 border-gray-200 pl-2 italic text-text-muted">
									{quote}
								</blockquote>
							</Field>
						)}
						{/* A computed figure: the working, and what the answer called
						    it, since the card is named by its family and role. */}
						{/* Plain words: the card already says what KIND of computed
						    figure it is (cushion, scenario, subtotal), so the panel
						    only adds how it was worked out and what it was called. */}
						{formula && (
							<Field label={tied ? "Recalculated as" : "Calculated as"}>
								<code className="break-all font-mono text-xs">{formula}</code>
							</Field>
						)}
						{qualifier && <Field label="Assumption">{qualifier}</Field>}
						{(derived || tied) && sourceLabel && sourceLabel !== node.label && (
							<Field label="In the answer">{sourceLabel}</Field>
						)}
						{/* A citation whose document the room layer could not name
						    still needs a way in. */}
						{node.href && !document && (
							<Field label="Citation">
								<a
									href={node.href}
									className="cursor-pointer font-medium text-[#003a39] underline decoration-[rgba(0,76,75,0.35)] decoration-[1.5px] underline-offset-2 hover:bg-[rgba(0,76,75,0.1)] rounded-[3px]"
									onClick={(event) => {
										event.preventDefault();
										onOpenHref(node.href as string, filename ?? undefined);
									}}
								>
									Source
								</a>
							</Field>
						)}
					</>
				)}

			</div>
		</aside>
	);
};

export default ContextGraphNodePanel;
